import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../../DesignSystem/Breadcrumb/Breadcrumb";
import EditAdviserDetails from "./EditAdviserDetails";
import {
  buildEditAdviserForm,
  saveEditAdviserForm,
  subscribeEditAdviserForm,
} from "../../../../../context/AdviserDetailsCrud";
import { getEditAdviserAuditChanges } from "../AuditLogs/auditHelpers";
import { logAuditChanges } from "../AuditLogs/auditLogger";
import { useUserEmail } from "../../../../../context/UserEmailContext";
import { notifyStaffAdviserDetailsUpdated } from "../../../../../context/AdviserDetailsNotifications";

export default function EditAdviserDetailsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { userEmail } = useUserEmail();

  const rawKey =
    params.adviserKey || params.id || params.email || params.adviserId || "";
  const adviserKey = rawKey ? decodeURIComponent(rawKey) : "";

  const [details, setDetails] = useState(null);
  const [form, setForm] = useState(buildEditAdviserForm());
  const [originalForm, setOriginalForm] = useState(buildEditAdviserForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const breadcrumbItems = useMemo(
    () => [
      { label: "Dashboard", to: "/my-dashboard" },
      { label: "My Profile", to: "/my-profile" },
      { label: "Edit Details" },
    ],
    [],
  );

  useEffect(() => {
    if (!adviserKey) return;

    const unsub = subscribeEditAdviserForm(adviserKey, ({ details, form }) => {
      setDetails(details);
      setForm(form);
      setOriginalForm(form);
      setLoading(false);
    });

    return unsub;
  }, [adviserKey]);

const handleSave = useCallback(async () => {
  try {
    setSaving(true);
    setSaveError("");

    const adviserId =
      details?.adviserId ||
      adviserKey ||
      details?.adviserDoc?.email ||
      details?.email ||
      state?.header?.email ||
      state?.email;

    if (!adviserId) {
      throw new Error("Adviser id/email not found.");
    }

    const auditChanges = getEditAdviserAuditChanges({
      originalForm,
      updatedForm: form,
    });

    await saveEditAdviserForm(adviserId, form);

    if (auditChanges.length) {
      await logAuditChanges({
        entity: "adviser",
        entityId: adviserId,
        changes: auditChanges,
        extra: {
          page: "EditAdviserDetailsPage",
        },
      });

      try {
        await notifyStaffAdviserDetailsUpdated({
          adviserId,
          adviserName: [
            form?.personalDetails?.forename,
            form?.personalDetails?.surname,
          ]
            .filter(Boolean)
            .join(" "),
          adviserEmail:
            form?.personalDetails?.emailAddress ||
            form?.adviserDoc?.email ||
            details?.email ||
            adviserId,
          updatedBy: userEmail || "",
          changes: auditChanges,
        });
      } catch (emailErr) {
        console.error(
          "Adviser details saved, but staff notification failed:",
          emailErr,
        );
      }
    }

    navigate(-1);
  } catch (err) {
    setSaveError(err.message || "Failed to save adviser details.");
  } finally {
    setSaving(false);
  }
}, [details, adviserKey, form, originalForm, navigate, state, userEmail]);
  return (
    <div className="Main-Page">
      <Helmet>
        <title>Edit Adviser Details</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container Lavender AdviserDetailsPage">
        <Breadcrumb items={breadcrumbItems} />
        <div className="smProfilePage">
          {loading ? (
            <div className="smLoading Content-Small">Loading profile…</div>
          ) : (
            <>
              {saveError ? (
                <div
                  className="Content-Small"
                  style={{ color: "red", marginBottom: 12 }}
                >
                  {saveError}
                </div>
              ) : null}

              <EditAdviserDetails
                form={form}
                setForm={setForm}
                saving={saving}
                onSave={handleSave}
                onCancel={() => navigate(-1)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
